import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  Cell,
} from "recharts"
import { geoMercator, geoPath, geoCentroid } from "d3-geo"
import { scaleLinear } from "d3-scale"

const EU_GEOJSON_URL =
  "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson"

const CHART_COLORS = {
  PL: "#dc2626",
  DE: "#1d4ed8",
  CZ: "#059669",
  HU: "#d97706",
  AT: "#7c3aed",
  FR: "#0891b2",
  ES: "#be185d",
  IT: "#65a30d",
  NL: "#ea580c",
  EU27_2020: "#64748b",
}

const TAB_IDS = ["overview", "trends", "affordability", "fun", "data"]

function periodSortKey(period) {
  const q = String(period).match(/(\d{4})-Q(\d)/i)
  if (q) return Number(q[1]) * 10 + Number(q[2])
  const m = String(period).match(/(\d{4})-(\d{2})/)
  if (m) return Number(m[1]) * 100 + Number(m[2])
  const y = String(period).match(/^(\d{4})$/)
  if (y) return Number(y[1]) * 100
  return 0
}

function formatSyncTime(iso) {
  if (!iso) return "—"
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-2xl border border-border bg-surface p-5 ${accent ? "ring-2 ring-primary/20" : ""}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-2 font-display text-3xl font-extrabold tracking-tight text-ink">{value ?? "—"}</p>
      {sub && <p className="mt-1 text-sm text-muted">{sub}</p>}
    </div>
  )
}

const EUROPE_BOUNDS = { minLon: -25, maxLon: 45, minLat: 34, maxLat: 72 }

function inEurope([lon, lat]) {
  return (
    lon >= EUROPE_BOUNDS.minLon &&
    lon <= EUROPE_BOUNDS.maxLon &&
    lat >= EUROPE_BOUNDS.minLat &&
    lat <= EUROPE_BOUNDS.maxLat
  )
}

/** Drop overseas polygons (e.g. French Guiana) so fitExtent stays on Europe. */
function mainlandFeature(feature) {
  const { type, coordinates } = feature.geometry || {}
  if (!coordinates) return null

  if (type === "Polygon") {
    return inEurope(geoCentroid(feature)) ? feature : null
  }

  if (type === "MultiPolygon") {
    const kept = coordinates.filter((polygon) => {
      const part = { type: "Feature", geometry: { type: "Polygon", coordinates: polygon }, properties: {} }
      return inEurope(geoCentroid(part))
    })
    if (!kept.length) return null
    return { ...feature, geometry: { type: "MultiPolygon", coordinates: kept } }
  }

  return feature
}

function featureCountryCode(feature) {
  const p = feature.properties || {}
  return p["ISO3166-1-Alpha-2"] || p.ISO_A2 || p.ISO_A2_EH || null
}

function featureCountryName(feature) {
  const p = feature.properties || {}
  return p.name || p.ADMIN || p.NAME || featureCountryCode(feature)
}

function EuHpiMap({ rows, period }) {
  const { t } = useTranslation()
  const [geojson, setGeojson] = useState(null)
  const [hover, setHover] = useState(null)
  const [error, setError] = useState(false)

  const width = 720
  const height = 480

  useEffect(() => {
    fetch(EU_GEOJSON_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setGeojson)
      .catch(() => setError(true))
  }, [])

  const valueByCountry = useMemo(() => {
    const map = {}
    ;(rows || []).forEach((r) => {
      map[r.country] = r.value
    })
    return map
  }, [rows])

  const values = Object.values(valueByCountry).filter((v) => v != null)
  const domain = values.length ? [Math.min(...values), Math.max(...values)] : [90, 180]

  const colorScale = useMemo(
    () => scaleLinear().domain(domain).range(["#fef3c7", "#b45309", "#7c2d12"]).clamp(true),
    [domain]
  )

  const europeFeatures = useMemo(() => {
    if (!geojson?.features) return []
    return geojson.features
      .filter((f) => {
        const iso = featureCountryCode(f)
        return iso && iso !== "-99" && valueByCountry[iso] != null
      })
      .map(mainlandFeature)
      .filter(Boolean)
  }, [geojson, valueByCountry])

  const projection = useMemo(() => {
    if (!europeFeatures.length) return null
    return geoMercator().fitSize([width - 64, height - 48], {
      type: "FeatureCollection",
      features: europeFeatures,
    })
  }, [europeFeatures])

  const pathGenerator = useMemo(() => (projection ? geoPath(projection) : null), [projection])

  if (error) {
    return <p className="text-sm text-muted">{t("polandVsEurope.euMapUnavailable")}</p>
  }

  if (!geojson) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-8 text-center text-sm text-muted">
        {t("common.loading.euHousingMap")}
      </div>
    )
  }

  if (!pathGenerator || !europeFeatures.length) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-8 text-center text-sm text-muted">
        {t("polandVsEurope.euMapDatasetUnavailable")}
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-4 sm:p-6">
      <h3 className="font-display text-lg font-bold">{t("polandVsEurope.hpiMapTitle")}</h3>
      <p className="mt-1 text-sm text-muted">
        {t("polandVsEurope.hpiMapDescription", { period: period || "latest" })}
      </p>
      <div className="relative mt-4">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          className="mx-auto w-full max-w-3xl"
          role="img"
          aria-label={t("polandVsEurope.hpiMapAria")}
        >
          {europeFeatures.map((feature) => {
            const iso = featureCountryCode(feature)
            const val = valueByCountry[iso]
            const name = featureCountryName(feature)
            return (
              <path
                key={iso}
                d={pathGenerator(feature)}
                fill={colorScale(val)}
                stroke="#fff"
                strokeWidth={0.6}
                onMouseEnter={() => setHover({ iso, name, val })}
                onMouseLeave={() => setHover(null)}
                className="cursor-default transition-opacity hover:opacity-80"
              />
            )
          })}
        </svg>
        {hover && (
          <div className="pointer-events-none absolute left-4 top-4 rounded-xl border border-border bg-white/95 px-3 py-2 text-sm shadow-lg">
            <p className="font-bold">{hover.name}</p>
            <p className="font-mono text-primary">{hover.val?.toFixed(1) ?? "—"}</p>
          </div>
        )}
      </div>
      <p className="mt-3 text-xs text-muted">
        {t("polandVsEurope.boundaries")}{" "}
        <a href="https://github.com/datasets/geo-countries" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
          datasets/geo-countries
        </a>{" "}
        · {t("polandVsEurope.onlyImportedCountries")}
      </p>
    </div>
  )
}

function TrendChart({ timeseries, datasetId }) {
  const { t } = useTranslation()
  const hpiSeries = timeseries?.find((series) => series.id === datasetId) || timeseries?.[0]
  const [hidden, setHidden] = useState({})

  const chartData = useMemo(() => {
    if (!hpiSeries?.series?.length) return []

    const periodSet = new Set()
    hpiSeries.series.forEach((s) => s.points.forEach((p) => periodSet.add(p.period)))
    const periods = [...periodSet].sort((a, b) => periodSortKey(a) - periodSortKey(b))

    return periods.map((period) => {
      const row = { period }
      hpiSeries.series.forEach((s) => {
        const pt = s.points.find((p) => p.period === period)
        if (pt) row[s.country] = pt.value
      })
      return row
    })
  }, [hpiSeries])

  if (!chartData.length) {
    return <p className="text-sm text-muted">{t("polandVsEurope.notEnoughTrendData")}</p>
  }

  const toggle = (code) => setHidden((h) => ({ ...h, [code]: !h[code] }))

  return (
    <div className="rounded-2xl border border-border bg-surface p-4 sm:p-6">
      <h3 className="font-display text-lg font-bold">{hpiSeries?.label || t("polandVsEurope.trendDefault")}</h3>
      <p className="mt-1 text-sm text-muted">{t("polandVsEurope.trendDescription")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {hpiSeries.series.map((s) => (
          <button
            key={s.country}
            type="button"
            onClick={() => toggle(s.country)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition ${
              hidden[s.country] ? "bg-border text-muted line-through" : "bg-primary/10 text-primary"
            }`}
          >
            {s.name}
          </button>
        ))}
      </div>
      <div className="mt-4 h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} width={44} />
            <Tooltip labelFormatter={(l) => t("common.period", { period: l })} />
            <ReferenceLine y={100} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: t("charts.baseline2015"), fontSize: 10 }} />
            <Legend />
            {hpiSeries.series.map((s) =>
              hidden[s.country] ? null : (
                <Line
                  key={s.country}
                  type="monotone"
                  dataKey={s.country}
                  name={s.name}
                  stroke={CHART_COLORS[s.country] || "#64748b"}
                  strokeWidth={s.country === "PL" ? 3 : 2}
                  dot={false}
                  connectNulls
                />
              )
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function RankingBar({ rows, period }) {
  const { t } = useTranslation()
  const data = (rows || [])
    .filter((r) => r.country !== "EU27_2020")
    .slice(0, 14)
    .map((r) => ({ name: r.name, value: r.value, country: r.country }))

  if (!data.length) return null

  return (
    <div className="rounded-2xl border border-border bg-surface p-4 sm:p-6">
      <h3 className="font-display text-lg font-bold">{t("polandVsEurope.rankingTitle")}</h3>
      <p className="mt-1 text-sm text-muted">{t("polandVsEurope.rankingDescription", { period })}</p>
      <div className="mt-4 h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} domain={[80, "auto"]} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={88} />
            <Tooltip formatter={(v) => [v.toFixed(1), t("common.index")]} />
            <ReferenceLine x={100} stroke="#94a3b8" strokeDasharray="4 4" />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.map((entry) => (
                <Cell key={entry.country} fill={entry.country === "PL" ? "#dc2626" : "#3b82f6"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function DatasetTable({ snapshot }) {
  const { t } = useTranslation()
  if (!snapshot?.rows?.length) return null

  return (
    <div className="data-table-wrap mt-6">
      <table className="data-table w-full sm:min-w-[28rem]">
        <thead>
          <tr>
            <th scope="col">{t("common.country")}</th>
            <th scope="col" className="text-right">{t("common.value")}</th>
          </tr>
        </thead>
        <tbody>
          {snapshot.rows.map((row) => (
            <tr key={row.country} className={row.country === "PL" ? "bg-primary/5" : ""}>
              <td className="font-bold">
                {row.name}
                {row.country === "PL" && (
                  <span className="ml-2 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                    {t("common.poland")}
                  </span>
                )}
              </td>
              <td className="text-right font-mono font-semibold">{row.formatted}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FunMetricCard({ metric }) {
  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-surface to-primary/5 p-6">
      <div className="flex items-start gap-3">
        <span className="text-3xl" aria-hidden="true">
          {metric.emoji}
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted">{metric.title}</p>
          <p className="mt-1 font-display text-3xl font-extrabold tracking-tight">
            {metric.value} <span className="text-lg font-bold text-muted">{metric.unit}</span>
          </p>
        </div>
      </div>
      <p className="mt-4 text-sm text-ink leading-relaxed">{metric.detail}</p>
      {metric.footnote && <p className="mt-2 text-xs text-muted">{metric.footnote}</p>}
    </div>
  )
}

function ProvenancePanel({ provenance }) {
  const { t } = useTranslation()
  if (!provenance) return null

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-surface p-6">
        <h3 className="font-display text-lg font-bold">{t("polandVsEurope.provenance.title")}</h3>
        <p className="mt-2 text-sm text-muted leading-relaxed max-w-2xl">
          {t("polandVsEurope.provenance.description")}
        </p>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <dt className="font-bold text-muted">{t("polandVsEurope.provenance.source")}</dt>
            <dd className="mt-1">
              <a href={provenance.api_base} className="text-primary hover:underline break-all" target="_blank" rel="noopener noreferrer">
                Eurostat
              </a>
            </dd>
          </div>
          <div>
            <dt className="font-bold text-muted">{t("polandVsEurope.provenance.lastUpdated")}</dt>
            <dd className="mt-1">{formatSyncTime(provenance.last_sync_at)}</dd>
          </div>
        </dl>
      </div>

      <div className="data-table-wrap">
        <table className="data-table w-full sm:min-w-[36rem] text-sm">
          <thead>
            <tr>
              <th scope="col">{t("polandVsEurope.provenance.indicator")}</th>
              <th scope="col">{t("polandVsEurope.provenance.coverage")}</th>
              <th scope="col">{t("polandVsEurope.provenance.latestPeriod")}</th>
            </tr>
          </thead>
          <tbody>
            {(provenance.datasets || []).map((ds) => (
              <tr key={ds.id}>
                <td className="font-bold">{ds.label}</td>
                <td className="whitespace-nowrap text-muted">
                  {ds.period_min && ds.period_max ? `${ds.period_min} → ${ds.period_max}` : "—"}
                </td>
                <td>{ds.latest_period || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function PolandVsEuropeDashboard() {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [tab, setTab] = useState("overview")
  const [trendDataset, setTrendDataset] = useState("prc_hpi_q")

  useEffect(() => {
    fetch("/api/v1/comparisons/europe")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true))
  }, [])

  if (error) {
    return (
      <div className="rounded-2xl border border-negative/30 bg-negative/5 p-8 text-center">
        <p className="font-semibold text-negative">{t("common.errors.comparisonFailed")}</p>
        <p className="mt-2 text-sm text-muted">{t("common.errors.comparisonFailedHint")}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-10 text-center text-sm text-muted">
        {t("common.loading.europeDashboard")}
      </div>
    )
  }

  const { headline, datasets, timeseries, rankings, fun_metrics, provenance, warsaw_anchor } = data
  const hpiSnapshot = datasets?.house_price_index

  return (
    <div className="space-y-10">
      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t("polandVsEurope.kpi.polandHpi")}
          value={headline?.poland_hpi?.toFixed(1)}
          sub={headline?.period ? t("polandVsEurope.kpi.latestPeriod", { period: headline.period }) : null}
          accent
        />
        <KpiCard
          label={t("polandVsEurope.kpi.vsEuAverage")}
          value={headline?.vs_eu_pct != null ? `${headline.vs_eu_pct > 0 ? "+" : ""}${headline.vs_eu_pct}%` : null}
          sub={headline?.eu_hpi ? t("polandVsEurope.kpi.euIndex", { value: headline.eu_hpi.toFixed(1) }) : null}
        />
        <KpiCard
          label={t("polandVsEurope.kpi.vsGermany")}
          value={headline?.vs_germany_pct != null ? `${headline.vs_germany_pct > 0 ? "+" : ""}${headline.vs_germany_pct}%` : null}
          sub={t("polandVsEurope.kpi.nominalIndex")}
        />
        <KpiCard
          label={t("polandVsEurope.kpi.euRank")}
          value={headline?.rank ? `#${headline.rank}` : null}
          sub={headline?.country_count ? t("polandVsEurope.kpi.ofCountries", { count: headline.country_count }) : null}
        />
      </div>

      {headline?.growth_since_2015 != null && (
        <p className="max-w-3xl text-sm text-muted">
          {t("polandVsEurope.growthSince2015Prefix")}{" "}
          <strong className="text-ink">
            {t("polandVsEurope.growthIndexPoints", { points: headline.growth_since_2015 })}
          </strong>
          {warsaw_anchor && (
            <>
              {" "}
              {t("polandVsEurope.warsawAnchorPrefix")}{" "}
              <strong className="text-ink font-mono">
                {new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(
                  warsaw_anchor.price_per_m2
                )}
                /m²
              </strong>{" "}
              {t("polandVsEurope.warsawAnchorSuffix", { source: warsaw_anchor.source, period: warsaw_anchor.period })}
            </>
          )}
        </p>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-1">
        {TAB_IDS.map((tabId) => (
          <button
            key={tabId}
            type="button"
            onClick={() => setTab(tabId)}
            className={`rounded-t-lg px-4 py-2 text-sm font-bold transition ${
              tab === tabId ? "bg-primary text-white" : "text-muted hover:text-ink hover:bg-surface"
            }`}
          >
            {t(`polandVsEurope.tabs.${tabId}`)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-10">
          <EuHpiMap rows={hpiSnapshot?.rows} period={hpiSnapshot?.period} />
          <RankingBar rows={rankings?.by_hpi} period={rankings?.period} />
          <div>
            <h3 className="font-display text-xl font-extrabold">{hpiSnapshot?.label}</h3>
            <p className="mt-1 text-sm text-muted">{hpiSnapshot?.description}</p>
            <DatasetTable snapshot={hpiSnapshot} />
          </div>
        </div>
      )}

      {tab === "trends" && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {(timeseries || []).map((ts) => (
              <button
                key={ts.id}
                type="button"
                onClick={() => setTrendDataset(ts.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                  trendDataset === ts.id ? "bg-primary text-white" : "bg-surface border border-border text-muted"
                }`}
              >
                {ts.label}
              </button>
            ))}
          </div>
          <TrendChart timeseries={timeseries} datasetId={trendDataset} />
        </div>
      )}

      {tab === "affordability" && (
        <div className="space-y-10">
          {["rent_index", "cost_overburden", "housing_deprivation", "tenure_status", "real_house_price_index"].map((key) => {
            const snap = datasets?.[key]
            if (!snap?.rows?.length) return null
            return (
              <div key={key}>
                <h3 className="font-display text-xl font-extrabold">{snap.label}</h3>
                <p className="mt-1 text-sm text-muted">
                  {snap.description} · {snap.id} · {snap.period}
                </p>
                <DatasetTable snapshot={snap} />
              </div>
            )
          })}
        </div>
      )}

      {tab === "fun" && (
        <div className="space-y-6">
          <p className="max-w-2xl text-sm text-muted">
            {t("polandVsEurope.funIntro")}
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {(fun_metrics || []).map((m) => (
              <FunMetricCard key={m.id} metric={m} />
            ))}
          </div>
          {!fun_metrics?.length && (
            <p className="text-sm text-muted">{t("polandVsEurope.funUnavailable")}</p>
          )}
        </div>
      )}

      {tab === "data" && <ProvenancePanel provenance={provenance} />}
    </div>
  )
}
